import Link from "next/link";
import {
  ArrowRight,
  BarChart3,
  ClipboardCheck,
  Dumbbell,
  Route,
  ShieldCheck,
} from "lucide-react";
import Reveal from "@/components/ui/Reveal";
import EnterpriseJourney from "./EnterpriseJourney";

/**
 * The five steps, shared by the home page (this component and the Assess → Measure strip in
 * InaraXDifference) and the /how-it-works page. The ids are anchors on both pages; the
 * navbar's Product menu links to them on /how-it-works.
 */
export const workflowSteps = [
  {
    id: "assess-skills",
    verb: "Assess",
    label: "Assess skills",
    Icon: ClipboardCheck,
    title: "Assess your workforce's AI capability.",
    body: "A short AI assessment maps what each person already knows, so nobody starts in the wrong place.",
    points: [
      "Baseline for every employee, team and role",
      "Skill maps your L&D team can act on",
      "No one starts in the wrong place",
    ],
  },
  {
    id: "build-learning-paths",
    verb: "Personalize",
    label: "Build learning paths",
    Icon: Route,
    title: "Map learning to role and your business needs.",
    body: "inaraX turns your goals, roles and seniority levels into a personalized path for every employee.",
    points: [
      "Paths by role, seniority and goal",
      "Aligned to your business priorities",
      "Adapts as each person progresses",
    ],
  },
  {
    id: "learn-and-practice",
    verb: "Practice",
    label: "Learn & practice",
    Icon: Dumbbell,
    title: "Learn on realistic, role-specific work.",
    body: "People practise on scenarios from their own field, with an AI tutor that steps in when they're stuck.",
    points: [
      "Scenarios drawn from each function",
      "AI tutor support when people are stuck",
      "Short sessions that fit around work",
    ],
  },
  {
    id: "human-expert-validation",
    verb: "Validate",
    label: "Human expert validation",
    Icon: ShieldCheck,
    title: "Validated by human experts.",
    body: "Industry experts review projects and assessments, so skills are verified, not just completed.",
    points: [
      "Industry experts review projects",
      "Skills verified, not just completed",
      "A credential you can trust",
    ],
  },
  {
    id: "measure-outcomes",
    verb: "Measure",
    label: "Measure outcomes",
    Icon: BarChart3,
    title: "Measure what changed.",
    body: "Your L&D team tracks progress, completion and capability gains across every team.",
    points: [
      "Progress and completion by team",
      "Capability gains over time",
      "Reporting for leadership",
    ],
  },
] as const;

/**
 * The enterprise workflow on the home page, rendered inside InaraXDifference (so no <section>
 * shell of its own; it inherits that section's background and frame): the journey diagram
 * from Book a Consultation to Measure Impact, then a link to the full workflow. Replaces the three-step learner journey
 * that used to sit in StartsWithYou.
 */
export default function EnterpriseWorkflow() {
  return (
    <div id="how-it-works" className="scroll-mt-24 pt-size-xl">
      <EnterpriseJourney />

      <Reveal className="flex flex-col items-center justify-center gap-size-md pt-size-lg">
        <Link
          href="/how-it-works"
          className="group inline-flex items-center gap-1.5 font-label-md text-label-md font-bold text-mkt-primary"
        >
          Explore the full workflow
          <ArrowRight
            aria-hidden="true"
            className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
          />
        </Link>
      </Reveal>
    </div>
  );
}
